import axios from "axios";
import styled from "styled-components";
import {
  Box,
  Loader,
  Panel,
  Text,
  Flex,
  Button,
  BarChart,
  StreamCollection,
  EmptyState,
  formatDate,
  IconButton,
} from "@coaching-culture/ui";
import React, { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { FaBrain, FaChevronDown, FaChevronUp, FaPrint } from "react-icons/fa";
import ContentContainer from "./ContentContainer";
import {
  CompletedFormItem,
  ModuleAttempt,
  ModuleAttemptSummary,
  ScoreSet,
} from "@coaching-culture/types";
import ReactToPrint from "react-to-print";

export const ProfileBarChart = ({
  color,
  scores,
}: {
  color: string;
  scores: ScoreSet;
}) => {
  const data: StreamCollection[] = scores.groups.map((x) => ({
    label: x.name,
    streams: [{ label: "", value: x.score }],
  }));

  return (
    <BarChart xAxis="" yAxis="" streamLabels={[""]} color={color} data={data} />
  );
};

const Section = styled.section`
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  padding: 18px;
`;

const ModuleIcon = styled.img`
  width: 28px;
  margin-right: 18px;
`;

const attemptNumber = (
  attempt: ModuleAttemptSummary,
  attempts: ModuleAttemptSummary[],
) =>
  attempts
    .slice()
    .reverse()
    .filter((x) => x.module.id === attempt.module.id)
    .findIndex((x) => x.id === attempt.id) + 1;

const attemptCount = (
  attempt: ModuleAttemptSummary,
  attempts: ModuleAttemptSummary[],
) => attempts.filter((x) => x.module.id === attempt.module.id).length;

const printItem = (item: CompletedFormItem) => {
  if (item.type === "heading" || item.type === "content") {
    return null;
  } else if (item.type === "rating") {
    return (
      <Box mb={2}>
        <Text fontWeight={600} fontStyle="italic">
          {item.content}
        </Text>
        {item.rating != null && <Text>{item.rating}</Text>}
      </Box>
    );
  } else {
    return (
      <Box mb={2}>
        <Text fontWeight={600} fontStyle="italic">
          {item.content}
        </Text>
        {item.answer && <Text>{item.answer}</Text>}
      </Box>
    );
  }
};

const MyMindset = () => {
  const [attempts, setAttempts] = useState<ModuleAttemptSummary[] | null>(null);
  const [shownAttempt, setShownAttempt] = useState<string | null>(null);
  const [shownAttemptData, setShownAttemptData] =
    useState<ModuleAttempt | null>(null);
  const outcomesRef = useRef();

  useEffect(() => {
    axios.get("/api/mindset/attempts").then(({ data }) => {
      setAttempts(data);
    });
  }, []);

  useEffect(() => {
    setShownAttemptData(null);
    if (shownAttempt != null) {
      axios.get(`/api/mindset/attempts/${shownAttempt}`).then(({ data }) => {
        setShownAttemptData(data);
      });
    }
  }, [shownAttempt]);

  const showAttempt = (attempt: ModuleAttemptSummary) => {
    if (shownAttempt === attempt.id) {
      setShownAttempt(null);
    } else {
      setShownAttempt(attempt.id);
    }
  };

  const loading = attempts == null;

  return (
    <Panel mt={5}>
      {loading ? (
        <Loader />
      ) : attempts.length === 0 ? (
        <EmptyState icon={FaBrain} title="No attempts yet">
          Your results will appear here when you start a Mindset module.
        </EmptyState>
      ) : (
        attempts.map((attempt) => (
          <div>
            <Section>
              <Flex
                alignItems={["stretch", "center"]}
                flexDirection={["column", "row"]}
              >
                <Flex alignItems="center" mb={[1, 0]} flex="1">
                  <ModuleIcon
                    src={
                      process.env.REACT_APP_STATIC_ROOT +
                      "/" +
                      attempt.module.icon
                    }
                  />
                  <Text fontWeight={600} color="black">
                    {attempt.module.name}
                  </Text>
                </Flex>
                <Flex alignItems="stretch" flexDirection="column" flex="1">
                  <Text fontSize="small" color="textMuted">
                    {attempt.completed
                      ? `Completed ${formatDate(new Date(attempt.completed))}`
                      : "Not completed"}
                  </Text>
                  <Text fontSize="small" color="textMuted">
                    Attempt{" "}
                    {`${attemptNumber(attempt, attempts)}/${attemptCount(
                      attempt,
                      attempts,
                    )}`}
                  </Text>
                </Flex>
                <IconButton
                  icon={
                    shownAttempt === attempt.id ? FaChevronUp : FaChevronDown
                  }
                  color="grey"
                  aria-label={
                    shownAttempt === attempt.id
                      ? "Hide this attempt"
                      : "Show this attempt"
                  }
                  aria-expanded={shownAttempt === attempt.id}
                  onClick={() => showAttempt(attempt)}
                />
              </Flex>
            </Section>
            {shownAttempt === attempt.id ? (
              <>
                {shownAttemptData == null ? (
                  <Section>
                    <Loader />
                  </Section>
                ) : (
                  <>
                    <Section>
                      <Flex justifyContent="flex-end">
                        <ReactToPrint
                          trigger={() => <Button icon={FaPrint}>Print</Button>}
                          content={() => outcomesRef.current}
                        />
                      </Flex>
                    </Section>
                    <div ref={outcomesRef}>
                      <Section>
                        <Text
                          as="h3"
                          m={0}
                          mb={3}
                          fontSize="title"
                          fontWeight={600}
                        >
                          Profile outcome
                        </Text>
                        {shownAttemptData.profiles == null ? (
                          <Text color="textMuted">No profile yet</Text>
                        ) : (
                          shownAttemptData.profiles.map((x) => (
                            <>
                              {x.showAssessmentResults === "form-groups" && (
                                <Box maxWidth={1000} ml="auto" mr="auto">
                                  <ProfileBarChart
                                    color={shownAttemptData.module.mainColor}
                                    scores={shownAttemptData.assessmentScores}
                                  />
                                </Box>
                              )}
                              <ContentContainer content={x.content} />
                            </>
                          ))
                        )}
                      </Section>
                      <Section>
                        <Text
                          as="h3"
                          m={0}
                          mb={3}
                          fontSize="title"
                          fontWeight={600}
                        >
                          Self-reflection
                        </Text>
                        {shownAttemptData.assessment == null ? (
                          <Text color="textMuted">
                            Self-assessment not yet completed
                          </Text>
                        ) : (
                          shownAttemptData.assessment.items.map(printItem)
                        )}
                      </Section>
                      <Section>
                        <Text
                          as="h3"
                          m={0}
                          mb={3}
                          fontSize="title"
                          fontWeight={600}
                        >
                          Programme
                        </Text>
                        {shownAttemptData.programmeAnswers == null ? (
                          <Text color="textMuted">
                            {shownAttemptData.isLegacyAttempt
                              ? "Some data from previous platform versions is not currently available"
                              : "Programme not yet completed"}
                          </Text>
                        ) : shownAttemptData.programmeAnswers.length === 0 ? (
                          <Text color="textMuted">
                            No questions in programme
                          </Text>
                        ) : (
                          shownAttemptData.programmeAnswers.map((item) => (
                            <Box mb={2}>
                              <Text fontWeight={600} fontStyle="italic">
                                {item.question}
                              </Text>
                              <Text>{item.answer}</Text>
                            </Box>
                          ))
                        )}
                      </Section>
                      <Section>
                        <Text
                          as="h3"
                          m={0}
                          mb={3}
                          fontSize="title"
                          fontWeight={600}
                        >
                          Challenge
                        </Text>
                        {shownAttemptData.challenge == null ? (
                          <Text color="textMuted">
                            {shownAttemptData.isLegacyAttempt
                              ? "Some data from previous platform versions is not currently available"
                              : "Challenge not yet completed"}
                          </Text>
                        ) : (
                          shownAttemptData.challenge.items.map(printItem)
                        )}
                      </Section>
                    </div>
                  </>
                )}{" "}
              </>
            ) : null}
          </div>
        ))
      )}
    </Panel>
  );
};

export default MyMindset;
